import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { planStatusLabel } from './planConstants';

export default function PlatformPlansListPage() {
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [sort, setSort] = useState('sort_order');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (isSuper && includeDeleted) params.set('includeDeleted', 'true');
    try {
      const res = await platformApiFetch(`/api/platform/plans?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load plans');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, sort, order, includeDeleted, isSuper]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (col) => {
    if (sort === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(col);
      setOrder('asc');
    }
    setPage(0);
  };

  const applyFilters = () => {
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setPage(0);
  };

  const plans = (data && data.plans) || [];
  const total = (data && data.pagination && data.pagination.total) || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Plans</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/plans/new')}>
            New plan
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Name, code, description"
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {['draft', 'active', 'inactive', 'archived'].map((s) => (
                <MenuItem key={s} value={s}>
                  {planStatusLabel(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters} disabled={loading}>
            Apply
          </Button>
          {isSuper && (
            <FormControlLabel
              control={
                <Switch
                  checked={includeDeleted}
                  onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(0); }}
                />
              }
              label="Include deleted"
            />
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('code')}>
                    Code {sort === 'code' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('name')}>
                    Name {sort === 'name' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('status')}>
                    Status {sort === 'status' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell align="right">Monthly</TableCell>
                <TableCell align="right">Annual</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => toggleSort('sort_order')}>
                    Sort {sort === 'sort_order' ? (order === 'asc' ? '↑' : '↓') : ''}
                  </Button>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <code>{p.code}</code>
                    {p.deleted_at && (
                      <Chip size="small" label="Deleted" color="warning" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={planStatusLabel(p.status)} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">{Number(p.monthly_price).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(p.annual_price).toFixed(2)}</TableCell>
                  <TableCell>{p.sort_order}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/platform/plans/${p.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    No plans match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
}
